import { useState } from 'react';
import { Button, Modal, Upload as AntUpload, message } from 'antd';
import { Upload } from 'lucide-react';
import { ApplicationRolesService } from '../../../api';
import Papa from 'papaparse';

// Définition des props attendues par le composant
interface ImportRolesCsvProps {
  companyId: string;       // ID de l'entreprise concernée
  appId: string;          // ID de l'application concernée
  onSuccess: () => void;  // Callback appelé après un import réussi
}

export const ImportRolesCsv = ({ companyId, appId, onSuccess }: ImportRolesCsvProps) => {
  // États du composant
  const [visible, setVisible] = useState(false);          // Contrôle l'affichage de la modal
  const [fileList, setFileList] = useState<any[]>([]);    // Liste des fichiers sélectionnés
  const [isImporting, setIsImporting] = useState(false);  // Indicateur de chargement pendant l'import
  const [rolesPreview, setRolesPreview] = useState<string[]>([]); // Aperçu des rôles à importer

  /**
   * Vérifie la sécurité d'un fichier avant traitement
   * @param file - Le fichier à vérifier
   * @returns boolean - true si le fichier est sûr, false sinon
   */
  const checkFileSafety = (file: File): boolean => {
    // Liste des motifs suspects à détecter
    const unsafePatterns = [
      '=cmd|',       // Commandes Windows
      '<?php',       // Code PHP
      '<script',     // JavaScript
      'Macros',      // Macros Office
      'VBA',         // Visual Basic
      'eval(',       // Fonction JS dangereuse
      'ActiveX',     // Composant ActiveX
      '\\x',         // Encodage suspect
      'powershell',  // Commandes PowerShell
      '/bin/bash'    // Commandes bash
    ];
    
    // Vérifie si le nom du fichier contient des motifs dangereux
    if (unsafePatterns.some(pattern => file.name.includes(pattern))) {
      return false;
    }

    // Vérifie que la taille du fichier ne dépasse pas 1MB
    if (file.size > 1_000_000) {
      return false;
    }

    return true;
  };

  /**
   * Valide le contenu d'un fichier CSV
   * @param file - Le fichier CSV à valider
   * @returns Promise<{success: boolean, roles?: string[], error?: string}> 
   *          - Résultat de la validation avec les rôles extraits ou un message d'erreur
   */
  const validateCsv = async (file: File): Promise<{ success: boolean; roles?: string[]; error?: string }> => {
    // Vérification initiale de sécurité
    if (!checkFileSafety(file)) {
      return { success: false, error: 'File rejected: potential security risk detected' };
    }

    // Retourne une promesse pour le parsing asynchrone
    return new Promise((resolve) => {
      // Utilisation de PapaParse pour parser le CSV
      Papa.parse(file, {
        header: true,        // Utilise la première ligne comme en-têtes
        skipEmptyLines: true, // Ignore les lignes vides
        complete: (results) => {
          // Conversion des résultats en tableau d'objets
          const data = results.data as { RoleName?: string }[];
          
          // Vérifie que le fichier contient bien une colonne "RoleName"
          if (!data[0] || !('RoleName' in data[0])) {
            resolve({ success: false, error: 'Invalid CSV structure. Must contain "RoleName" column' });
            return;
          }

          // Filtre et valide les noms de rôles
          const roles = data
            .map(row => row.RoleName)  // Extrait les noms de rôles
            .filter(Boolean)          // Filtre les valeurs vides
            .filter(role => {
              // Validation de chaque rôle :
              // - Doit être une string
              // - 50 caractères max
              // - Caractères alphanumériques + _ et -
              return typeof role === 'string' && 
                     role.length <= 50 && 
                     /^[a-zA-Z0-9_-]+$/.test(role);
            }) as string[];
          
          // Vérifie qu'au moins un rôle valide a été trouvé
          if (roles.length === 0) {
            resolve({ success: false, error: 'No valid roles found in CSV file' });
            return;
          }

          // Retourne les rôles valides
          resolve({ success: true, roles });
        },
        error: () => {
          resolve({ success: false, error: 'Error parsing CSV file' });
        }
      });
    });
  };

  /**
   * Gère le processus d'import des rôles
   */
  const handleImport = async () => {
    // Vérifie qu'un fichier a été sélectionné
    if (fileList.length === 0) {
      message.warning('Please select a CSV file');
      return;
    }

    // Active l'état de chargement
    setIsImporting(true);
    const file = fileList[0].originFileObj;

    try {
      // Validation du fichier CSV
      const validation = await validateCsv(file);
      
      // Si validation échouée, affiche l'erreur
      if (!validation.success || !validation.roles) {
        message.error(validation.error || "Invalid CSV file");
        return;
      }

      const roles = validation.roles;
      let successCount = 0;  // Compteur de rôles importés avec succès
      let errorCount = 0;    // Compteur d'erreurs

      // Importe chaque rôle un par un
      for (const role of roles) {
        try {
          await ApplicationRolesService.create(companyId, appId, { name: role });
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Error during role creation${role}:`, error);
        }
      }

      // Affiche un message récapitulatif
      if (errorCount > 0) {
        message.warning(
          `Import complete with ${successCount} success and ${errorCount} failures.`
        );
      } else {
        message.success(`${successCount} successfully imported roles`);
      }

      // Réinitialise l'état et ferme la modal
      setVisible(false);
      setFileList([]);
      setRolesPreview([]);
      onSuccess(); // Exécute le callback de succès
    } catch (error: any) {
      message.error(error.message || "Error importing roles");
    } finally {
      setIsImporting(false); // Désactive l'état de chargement
    }
  };

  /**
   * Gère la sélection de fichier avant l'upload
   * @param file - Le fichier sélectionné
   * @returns boolean - false pour empêcher l'upload automatique
   */
  const beforeUpload = async (file: File) => {
    // Validation du fichier
    const validation = await validateCsv(file);
    
    // Si validation échouée, affiche l'erreur
    if (!validation.success) {
      message.error(validation.error || "Invalid CSV file");
      return false;
    }

    // Met à jour l'état avec le fichier validé
    setFileList([{ ...file, status: 'done' }]);
    // Affiche l'aperçu des rôles
    setRolesPreview(validation.roles || []);
    return false; // Empêche l'upload automatique
  };

  /**
   * Télécharge un template CSV exemple
   */
  const downloadTemplate = () => {
    // Contenu du template
    const csvContent = "RoleName\nadmin\nuser\nmanager\neditor";
    // Crée un blob avec le contenu CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    // Crée une URL pour le blob
    const url = URL.createObjectURL(blob);
    // Crée un élément <a> invisible pour déclencher le téléchargement
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roles_template.csv';
    a.click();
    // Nettoie l'URL
    URL.revokeObjectURL(url);
  };

  // Rendu du composant
  return (
    <>
      {/* Bouton pour ouvrir la modal d'import */}
      <Button 
        icon={<Upload size={16} />}
        onClick={() => setVisible(true)}
      >
        Import CSV
      </Button>

      {/* Modal d'import */}
      <Modal
        title="Import roles from a CSV file"
        open={visible}
        onCancel={() => {
          // Réinitialise l'état en fermant la modal
          setVisible(false);
          setFileList([]);
          setRolesPreview([]);
        }}
        onOk={handleImport}
        okButtonProps={{ loading: isImporting }}
        okText={isImporting ? 'Importing...' : 'Import'}
        cancelText="Cancel"
        width={600}
      >
        <div className="mb-4 space-y-4">
          <div>
            {/* Instructions pour l'utilisateur */}
            <p className="mb-2">Expected format: CSV file with “RoleName” column"</p>
            <p className="mb-4 text-sm text-gray-500">
              Example:<br />
              RoleName<br />
              admin<br />
              user<br />
              manager
            </p>
            
            {/* Composant Upload d'Ant Design */}
            <AntUpload
              accept=".csv"           // N'accepte que les CSV
              fileList={fileList}       // Liste des fichiers
              beforeUpload={beforeUpload} // Validation avant upload
              onRemove={() => {
                // Réinitialise lors de la suppression du fichier
                setFileList([]);
                setRolesPreview([]);
              }}
              maxCount={1}             // Limite à 1 fichier
            >
              <Button icon={<Upload />}>Select CSV file</Button>
            </AntUpload>
            
            {/* Lien pour télécharger le template */}
            <div className="flex justify-center mt-2">
              <Button
                type="link"
                onClick={downloadTemplate}
                className="text-sm"
              >
              Download template
              </Button>
            </div>
            
            {/* Informations supplémentaires */}
            <p className="text-xs text-gray-500 text-center mt-1">
              Max size: 1MB - Only single CSV files are accepted
            </p>
          </div>

          {/* Aperçu des rôles détectés */}
          {rolesPreview.length > 0 && (
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Preview ({rolesPreview.length} roles detected)
              </p>
              <div className="max-h-40 overflow-y-auto">
                <div className="flex flex-wrap gap-1">
                  {rolesPreview.map((role, index) => (
                    <span 
                      key={`role-preview-${index}`}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

